<script setup lang="ts">
import { NotAvailableProps } from "./NotAvailable.props"
import { FormKitNode } from "@formkit/core"
import { getSkuItemMetadata } from "~/utils/savedForLater/savedForLater"
import { SaveForLaterProductForAction } from "~/composables/useSavedForLater"
import { pushProductClickCtaEvent } from "~/utils/analytics/clickCta"
import { cleanObject } from "~/utils/analytics/analytics"
import { customPaths } from "~/utils/constants"

const props = defineProps<NotAvailableProps>()

const { data: result, pending } = useLazyFetch<any>(
  `/api/algolia/getRecommendations/recommend-7/${props.product.details.productId}`,
  {
    server: false
  }
)

const { openModal } = useModal()

const similarProductCode = useState("similar-product-code", () => "")

const handleSimilarProducts = (label: string) => {
  similarProductCode.value = props.product.details.productId
  openModal("similar-products")
  pushProductClickCtaEvent(label)
}

const similarProducts = useAlgoliaMappedProducts(result)

const product = computed((): SaveForLaterProductForAction => {
  return {
    productId: props.product.details.productId,
    skuId: props.product.details.skuId,
    metadata: getSkuItemMetadata(
      props.product.details?.gallery[0],
      props.product.details.slug,
      props.product.details.price,
      props.product.details.oldPrice,
      props.product.details.stockQuantity,
      props.product.description?.infos?.unitOfMeasure,
      props.product.details.expirationDate,
      props.product.details.isDeductable,
      cleanObject(props.product.gaItem)
    )
  }
})
</script>

<template>
  <div
    class="
      not-available-purchase-type-wrapper
      bg-grey-main
      flex flex-col
      gap-4
      rounded-lg
      p-4
    "
  >
    <div>
      <p class="mb-0 flex w-full items-center">
        <UtilsIcon
          name="CloseCircleFull.svg"
          color="negative-main"
          class="inline-block h-6 w-6"
          fetch-priority="high"
          :preload="true"
        />
        <span class="ms-2">
          <UtilsMarkdown
            :content="$t('productPage.purchaseType.notAvailable.title')"
            tag="span"
            class="beaver-bold md:pig"
          />
        </span>
      </p>
    </div>
    <p v-if="props.product?.singlePackOption" class="beaver-medium w-full">
      {{ $t("dialogsAndModals.productNotAvailable.singleProductOption") }}
      <AppLink
        :to="props.product?.singlePackOption?.slug"
        class="
          beaver-medium
          text-green-main
          hover:text-green-300
          cursor-pointer
          underline
        "
      >
        {{ props.product?.singlePackOption?.productName }}
      </AppLink>
    </p>

    <SkeletonAnimatedPlaceholder v-if="pending" height="34px" width="100%" />
    <p v-else-if="similarProducts.length" class="beaver-medium w-full">
      {{ $t("dialogsAndModals.productNotAvailable.otherProducts") }}
      <AppLink
        @click="
          () =>
            handleSimilarProducts(
              $t('dialogsAndModals.productNotAvailable.similarProducts')
            )
        "
        class="
          beaver-medium
          text-green-main
          hover:text-green-300
          cursor-pointer
          underline
        "
      >
        {{ $t("dialogsAndModals.productNotAvailable.similarProducts") }}
      </AppLink>
    </p>
  </div>
</template>
